import { LegalPage } from './LegalPage';

const CONTENT = `# Privacy Policy

**Last updated: 08 July 2026**

This Privacy Policy explains how **Forsa Ltd** ("**Forsa**", "**we**", "**us**", "**our**") collects, uses, shares and protects personal data in connection with the Taiga platform and the website at taigahedge.com. "**Taiga**" is a trading name of Forsa Ltd.

Forsa Ltd is a company registered in England and Wales under company number **17288614**, with its registered office at 71–75 Shelton Street, Covent Garden, London, WC2H 9JQ, United Kingdom.

We process personal data in accordance with the **UK General Data Protection Regulation (UK GDPR)** and the **Data Protection Act 2018**, as amended (including by the **Data (Use and Access) Act 2025**).

---

## 1. Scope, deployment model and our role

Taiga is **licensed software that is deployed and operated on the client's own infrastructure**. It is not a hosted, cloud, software-as-a-service (SaaS) or platform-as-a-service (PaaS) offering. As a result, our role under data-protection law is limited:

- **We act as a controller** for the personal data we handle in the course of our own business: visitors to and users of the taigahedge.com website; enquiries and support correspondence sent to us; and our business, licensing and client-relationship contacts.
- **We do not host or have routine access** to the personal data processed **within a deployed instance** of the Platform — for example, user accounts, login credentials, authentication secrets, roles, and operational or trading data. That data resides on the **client's own infrastructure**, under the **client's** control. For that data, the client (and not Forsa) is the controller.
- **Where a client engages us for support or maintenance** that requires access to its deployed instance, any personal data we thereby access is processed **only on the client's documented instructions** and under the data-processing terms agreed between us, under which the client is the controller and Forsa is the processor.

This Policy describes our activities **as a controller**. If you are an individual whose personal data is processed within a client's deployed instance of Taiga, the client is the controller — please contact that client to exercise your rights.

---

## 2. Personal data we collect (as controller)

Depending on how you interact with us, we may collect:

- **Contact and business data** — name, business email address, telephone number, job title, employer, and the details you provide when enquiring about, licensing, or discussing the Platform.
- **Website usage and technical data** — IP address, device and browser information, pages visited, referral data, and diagnostic information collected when you visit taigahedge.com.
- **Communications data** — correspondence with us, including enquiries and support requests sent to our contact addresses.
- **Cookies and similar technologies** on our website — see section 8.

We do not intentionally collect special-category personal data. Please do not submit such data to us except where strictly necessary and lawful.

> **Note on Platform user data.** Credentials, authenticator (TOTP) secrets, roles, session and access-log data for users of the Platform are created and stored **within the client's deployed instance on the client's infrastructure**. We do not collect or hold that data, except transiently where a client engages us for contracted support (see section 1).

---

## 3. How we use personal data and our lawful bases

We use the personal data we hold as controller for the following purposes:

| Purpose | Lawful basis (UK GDPR Art. 6) |
|---|---|
| Operating and securing the taigahedge.com website | Legitimate interests |
| Responding to enquiries and providing support | Legitimate interests; steps prior to a contract |
| Managing licensing and our client relationships | Performance of a contract; legitimate interests |
| Sending business communications you have requested or that are relevant to an existing relationship | Legitimate interests; consent where required |
| Complying with legal, tax, accounting and record-keeping obligations | Legal obligation |
| Establishing, exercising or defending legal claims | Legitimate interests |

Where we rely on **legitimate interests**, our interests are in operating and promoting a secure and lawful business; we have considered and balanced these against your rights and freedoms. You may object to processing based on legitimate interests (see section 7). Where we rely on **consent**, you may withdraw it at any time.

---

## 4. Who we share personal data with

We may share the personal data we hold as controller with:

- **Service providers** who support our own business, such as our website host, email provider, and professional advisers, acting on our instructions.
- **Authorities, regulators or law-enforcement bodies**, where required by law or to protect our rights.
- **A successor entity**, in connection with a merger, acquisition or reorganisation, subject to appropriate safeguards.

We do not sell personal data. We do not receive or share the operational data held within clients' deployed instances.

---

## 5. International transfers

Where we transfer the personal data we control outside the United Kingdom, we ensure an appropriate safeguard is in place, such as a UK adequacy regulation ("data bridge") for the destination country, the International Data Transfer Agreement (IDTA) or the UK Addendum to the EU Standard Contractual Clauses, or another lawful transfer mechanism.

Third-party services that may be integrated into a deployed instance of the Platform — for example, market-data or economic-calendar providers, or messaging services used for operational alerts — are configured and controlled by the client within the client's own environment. They process data on the client's behalf as part of the client's deployment, not as processors of the personal data that Forsa controls, and any resulting international transfers are the client's responsibility as controller.

---

## 6. Retention

Because the Platform is deployed on the client's own infrastructure, **we do not hold, and therefore do not retain, the operational, trading, credential or account data processed within a client's instance** — retention of that data is determined and controlled by the client.

For the personal data we do hold as controller (website, enquiry, support, licensing and business-contact records), we retain it only for as long as necessary for the purposes described in this Policy, and to meet our legal, tax and record-keeping obligations. We retain such data for **up to 10 years**, and in the case of contract and licensing records, for the duration of the relationship and up to 10 years thereafter. When data is no longer required, we securely delete or anonymise it.

---

## 7. Your rights

Subject to the conditions and exemptions in UK data-protection law, you have the right to:

- request **access** to your personal data;
- request **rectification** of inaccurate or incomplete data;
- request **erasure** of your data in certain circumstances;
- request **restriction** of processing;
- **object** to processing based on legitimate interests, and to direct marketing at any time;
- request **data portability**; and
- **withdraw consent** where processing is based on consent.

To exercise any of these rights in relation to data we control, contact us using the details in section 10. We may need to verify your identity, and we will respond within the timeframes required by law. For personal data processed **within a client's deployed instance**, the client is the controller — please contact that client.

---

## 8. Cookies and similar technologies

The taigahedge.com website uses only **strictly necessary and functional cookies** — those required for the website to operate securely and to remember basic preferences. We do **not** use cookies or similar technologies for advertising, marketing, cross-site tracking, profiling, or statistical/analytics purposes, and we do not track visitors for those purposes.

Under the Privacy and Electronic Communications Regulations (PECR), strictly necessary cookies do not require prior consent; we provide this information for transparency. Because we do not use non-essential cookies, no cookie consent banner or opt-out is required.

Any cookies or similar technologies used by a deployed instance of the Platform operate within the client's own environment and are the client's responsibility.

---

## 9. Security

We implement appropriate technical and organisational measures to protect the personal data we hold as controller.

The security of a **deployed instance** of the Platform, and of the personal data within it, is the **client's responsibility**, as the Platform runs on the client's own infrastructure. The Platform provides security features — such as access controls, multi-factor authentication, encryption of sensitive credentials at rest, and logging — for the client to configure and operate. No system is entirely secure, and users are responsible for safeguarding their own credentials and access.

---

## 10. Complaints and contact

If you have any question or concern about how we handle personal data, or you wish to exercise your rights, please contact us:

**Forsa Ltd** (trading as Taiga)
71–75 Shelton Street, Covent Garden, London, WC2H 9JQ, United Kingdom
Email: info@taigahedge.com
Telephone: +1 888 301 6002

**Your right to complain to us.** You may make a data-protection complaint to us directly. We will acknowledge your complaint within **30 days** of receipt and provide a full response without undue delay.

**Your right to complain to the regulator.** You also have the right to lodge a complaint with the UK Information Commissioner's Office (ICO) at ico.org.uk or by calling 0303 123 1113. We would, however, appreciate the chance to address your concerns first.

For all privacy matters, please use the general contact details above.

---

## 11. Changes to this Policy

We may update this Privacy Policy from time to time. The current version will be posted on the Platform with the "Last updated" date above. Where changes are material, we will take reasonable steps to notify affected individuals or clients.
`;

export function PrivacyPage() {
  return <LegalPage content={CONTENT} />;
}